
import React, { useEffect, useState } from "react";
import {
    mass_of_proton,
    mass_of_neutron,
    mass_of_electron,
    aV,
    aS,
    aC,
    aA,
    aP,
    conversion_amu_MeV
} from "../Functions/constants.js";
import {
    BE_func,
    mass_defect_func,
    liquid_drop_model,
    elements
} from "../Functions/index.js";
import { useGlobalState } from "../Components/Context.js";
import Typed from 'react-typed';
import IsotopeNotFound from "./IsotopeNotFound.js";
import { FaArrowLeft } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import IsotopeCannotBeCal from './IsotopeCannotBeCal.js';
import { getNuclide } from "../Services/nuclideData.js";

const CalulationScreen = () => {
    const [data, setData] = useState(undefined);
    const [loadError, setLoadError] = useState(null);
    const nav = useNavigate();
    const {
        Z,
        N
    } = useGlobalState();

    useEffect(() => {
        let active = true;

        setData(undefined);
        setLoadError(null);

        getNuclide(Number(Z), Number(N))
            .then((nuclide) => {
                if (active) {
                    setData(nuclide);
                }
            })
            .catch((error) => {
                if (active) {
                    setLoadError(error);
                }
            });

        return () => {
            active = false;
        };
    }, [Z, N]);

    if (loadError) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-[#6f263d] text-white p-8">
                <h1 className="text-3xl font-semibold text-center">
                    Unable to load the nuclear dataset
                </h1>
                <p className="mt-4 text-center">{loadError.message}</p>
                <button
                    type="button"
                    className="mt-6 bg-white text-[#6f263d] px-6 py-3 rounded"
                    onClick={() => nav('/')}
                >
                    Go back
                </button>
            </div>
        );
    }

    if (data === undefined) {
        return (
            <div className="flex flex-row items-center justify-center h-screen bg-[#6f263d] " >
                <div className="m-2 mb-3 self-center">
                    {/* <h1 className=" text-white text-3xl font-semibold ">Calculating<Typed className=" text-white text-3xl font-semibold " strings={["..."]} typeSpeed={120} backSpeed={140} loop /></h1> */}
                    {/* <h1 className=" text-white text-3xl font-semibold ">Calculating<Typed className=" text-white text-3xl font-semibold " strings={["..."]} typeSpeed={120} backSpeed={140} loop /></h1> */}
                    <h1 className=" text-white text-3xl font-semibold animate-bounce text-center"> Nuclide Search and Binding Energy Calculation <Typed className=" text-white text-3xl font-semibold " strings={["..."]} typeSpeed={120} backSpeed={140} loop /></h1>
                </div>
            </div>
        )
    }

    if (data === null) {
        return (
            <IsotopeNotFound />
        )
    }

    const atomic_mass = data.atomicMassU;
    const atomic_number = data.z;
    const neutron_number = data.n;
    const element = elements(data.symbol) + "-" + (atomic_number + neutron_number);
    const mass_number = atomic_number + neutron_number;
    
    if (
        data.atomicMassU === null ||
        data.bindingEnergyPerNucleonMeV === null
    ) {
        const name = `${elements(data.symbol)}-${mass_number}`;

        return (
            <IsotopeCannotBeCal element={name} z ={atomic_number} n ={neutron_number}  />
        )
    }


    const massDefect = mass_defect_func(atomic_mass, atomic_number, neutron_number);
    const experimentalEnergy = BE_func(atomic_mass, atomic_number, neutron_number);
    const modelEnergy = liquid_drop_model(atomic_number, neutron_number);

    return (
        <main className="results-page">
            <header className="results-header">
                <div className="results-shell">
                    <button type="button" className="results-back" onClick={() => nav('/')}><FaArrowLeft /> New calculation</button>
                    <p className="section-kicker section-kicker-light">Calculation results</p>
                    <h1>{element}</h1>
                    <dl className="nuclide-facts">
                        <div><dt>Z</dt><dd>{atomic_number} protons</dd></div>
                        <div><dt>N</dt><dd>{neutron_number} neutrons</dd></div>
                        <div><dt>A</dt><dd>{mass_number} nucleons</dd></div>
                    </dl>
                </div>
            </header>

            <section className="results-shell method-results-grid">
                <article className="result-method-card result-method-primary">
                    <p className="method-label">Experimental mass data</p>
                    <h2>Mass-Defect Approach</h2>
                    <p className="result-description">The measured atomic mass is compared with the combined masses of the constituent protons, neutrons, and electrons.</p>
                    <div className="result-formula">B.E. = Δm c<sup>2</sup></div>
                    <dl className="parameter-list">
                        <div><dt>Mass defect</dt><dd>{massDefect.toFixed(4)} amu</dd></div>
                        <div><dt>Atomic mass</dt><dd>{atomic_mass} amu</dd></div>
                        <div><dt>Proton mass</dt><dd>{mass_of_proton} amu</dd></div>
                        <div><dt>Neutron mass</dt><dd>{mass_of_neutron} amu</dd></div>
                        <div><dt>Electron mass</dt><dd>{mass_of_electron} amu</dd></div>
                        <div><dt>Conversion</dt><dd>1 amu = {conversion_amu_MeV} MeV/c<sup>2</sup></dd></div>
                    </dl>
                    <div className="result-summary">
                        <div><span>Total binding energy</span><strong>{experimentalEnergy.toFixed(4)} MeV</strong></div>
                        <div><span>Binding energy per nucleon</span><strong>{(experimentalEnergy / mass_number).toFixed(4)} MeV</strong></div>
                    </div>
                </article>

                <article className="result-method-card">
                    <p className="method-label">Semi-empirical estimate</p>
                    <h2>Liquid-Drop Model</h2>
                    <p className="result-description">The nucleus is modeled through volume, surface, Coulomb, asymmetry, and pairing contributions.</p>
                    <div className="result-formula result-formula-long">B.E. = a<sub>V</sub>A − a<sub>S</sub>A<sup>2/3</sup> − a<sub>C</sub>Z(Z−1)/A<sup>1/3</sup> − a<sub>A</sub>(A−2Z)<sup>2</sup>/A ± a<sub>P</sub>/A<sup>1/2</sup></div>
                    <dl className="parameter-list">
                        <div><dt>Volume coefficient</dt><dd>a<sub>V</sub> = {aV} MeV</dd></div>
                        <div><dt>Surface coefficient</dt><dd>a<sub>S</sub> = {aS} MeV</dd></div>
                        <div><dt>Coulomb coefficient</dt><dd>a<sub>C</sub> = {aC} MeV</dd></div>
                        <div><dt>Asymmetry coefficient</dt><dd>a<sub>A</sub> = {aA} MeV</dd></div>
                        <div><dt>Pairing coefficient</dt><dd>a<sub>P</sub> = {aP} MeV</dd></div>
                    </dl>
                    <div className="result-summary">
                        <div><span>Model binding energy</span><strong>{modelEnergy.toFixed(4)} MeV</strong></div>
                        <div><span>Model energy per nucleon</span><strong>{(modelEnergy / mass_number).toFixed(4)} MeV</strong></div>
                    </div>
                </article>
            </section>

            <section className="results-shell iaea-comparison">
                <span>IAEA Nuclear Data Services reference</span>
                <strong>{data.bindingEnergyPerNucleonMeV.toFixed(4)} MeV per nucleon</strong>
            </section>
        </main>
    )
}
export default CalulationScreen;
